import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../shared/StatusBadge';
import { Task } from '../../types';

interface RecentTasksProps {
  tasks?: Task[];
  /** When true, omit outer card chrome (parent provides section frame). */
  embedded?: boolean;
}

export default function RecentTasks({ tasks = [], embedded = false }: RecentTasksProps) {
  const { t } = useTranslation();

  const shellClass = embedded
    ? ''
    : 'bg-surface-card border border-hairline rounded-xl p-5 shadow-card';

  if (tasks.length === 0) {
    return (
      <div className={shellClass}>
        {!embedded ? (
          <h3 className="mb-3 text-sm font-medium text-ink">{t('dashboard.recentTasks')}</h3>
        ) : null}
        <p className="py-6 text-center text-sm text-muted">
          {t('dashboard.recentEmpty')}{' '}
          <Link to="/chat-test" className="text-primary hover:underline">
            {t('dashboard.recentChatLink')}
          </Link>
          {' '}
          {t('dashboard.recentHint')}{' '}
          <Link to="/tasks" className="text-primary hover:underline">
            {t('dashboard.recentTasksLink')}
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      {!embedded ? (
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-ink">{t('dashboard.recentTasks')}</h3>
          <Link to="/tasks" className="shrink-0 text-[11px] text-primary hover:underline">
            {t('common.viewAll')}
          </Link>
        </div>
      ) : (
        <div className="mb-3 flex justify-end">
          <Link to="/tasks" className="text-xs font-medium text-primary hover:underline">
            {t('common.viewAll')}
          </Link>
        </div>
      )}
      <div className="divide-y divide-app-border/50 rounded-lg border border-app-border/60 overflow-hidden">
        {tasks.slice(0, 8).map((task) => (
          <Link
            key={task.id}
            to={`/tasks/${task.id}`}
            className="flex items-center justify-between gap-3 bg-app-bg/40 px-4 py-3 transition-colors hover:bg-app-muted/40"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">{task.message}</p>
              <p className="mt-0.5 text-xs text-muted">
                {task.station} · {task.time}
              </p>
            </div>
            <StatusBadge
              variant={
                task.status === 'completed' ? 'success' : task.status === 'error' ? 'error' : 'info'
              }
              dot
              className="shrink-0"
            >
              {task.status}
            </StatusBadge>
          </Link>
        ))}
      </div>
    </div>
  );
}
